import React from 'react';
import { History, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PartyCard } from '@/components/ewayBill/shared';
import { ItemsTable } from '@/components/ewayBill/ItemsTable';
import { CustomerPick } from '@/components/ewayBill/CustomerPick';
import { DestinationPicker } from '@/components/ewayBill/DestinationPicker';

const UnitSelect = ({ units, value, onChange, testId }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value || null)} className="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs" data-testid={testId}>
    {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
  </select>
);

const TO_SRC = { transfer_out_pdf: 'Transfer Out PDF', manual_dc: 'Manual DC', outside_invoice_pdf: 'Customer Master' };
const toTitle = (d) => {
  if (d.to_source === 'jobber') return 'To · jobber (Jobber Master)';
  if (d.to_source !== 'erp') return d.to ? 'To · our warehouse' : 'To';
  const src = TO_SRC[d.erp_source] || (d.erp_source !== 'wms' ? 'ERP' : d.tally_kind === 'job_work' ? 'Jobber Master' : 'Customer Master');
  return `To · ${src}`;
};

/** Step 2 — Part A from the ERP (read only); sender unit, destination (warehouse / jobber) and sub supply type are picked here.
 * Inward bills: From = the supplier / job worker, To = our receiving unit. */
export const EwbReview = ({ draft: d, units, opts, setOpts, route }) => {
  const set = (k) => (v) => setOpts({ ...opts, [k]: v });
  const pickDest = d.doc_type === 'branch_transfer' && d.to_source !== 'erp';
  const inward = d.supply_type === 'Inward';
  const fromTitle = inward ? `From · ${d.inward_kind === 'jw_return' ? 'job worker' : 'supplier'} (inward)` : `From${d.from_source === 'erp' ? ' · ERP' : ' · sender unit'}`;
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-gray-200 px-3 py-2 text-xs text-gray-600" data-testid="ewb-erp-note">
        <Info className="w-4 h-4 shrink-0 text-[#2D5F4F]" />{d.erp_note}
      </div>
      {inward && (
        <p className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900" data-testid="ewb-inward-note">
          <b>Inward e-way bill</b> — we make it for the sender (supply type <b>Inward</b>). It always goes out as the GST portal JSON{route === 'tally' ? ', not through Tally' : ''}.
        </p>
      )}
      {d.doc_type === 'outside_invoice' && (
        <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="ewb-outside-note">
          <b>Invoice not done in WMS</b> — goods lines are the invoice's HSN table. It always goes out as the GST portal JSON{route === 'tally' ? ', not through Tally' : ''}.
        </p>
      )}
      <div className="grid md:grid-cols-4 gap-3 text-sm">
        <div><div className="text-[11px] uppercase text-gray-500">Document</div><div className="font-semibold" data-testid="ewb-review-doctype">{d.document_type}</div></div>
        <div><div className="text-[11px] uppercase text-gray-500">Number / date</div><div className="font-mono">{d.doc_no} · {d.doc_date}</div></div>
        <div>
          <div className="text-[11px] uppercase text-gray-500">Sub supply type</div>
          <select value={d.sub_supply_type} onChange={(e) => set('sub_supply_type')(e.target.value)} disabled={d.sub_supply_options.length < 2}
            className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid="ewb-sub-supply">
            {d.sub_supply_options.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {d.sub_supply_type === 'Others' && (
          <div><div className="text-[11px] uppercase text-gray-500">Reason (Others)</div>
            <Input value={opts.sub_supply_desc} onChange={(e) => set('sub_supply_desc')(e.target.value)} maxLength={20} className="h-9" data-testid="ewb-sub-desc" /></div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <PartyCard title={fromTitle} p={d.from} testId="ewb-review-from">
          {!inward && d.from_source !== 'erp' && units.length > 0 && <UnitSelect units={units} value={d.unit_id} onChange={set('unit_id')} testId="ewb-from-unit" />}
        </PartyCard>
        <PartyCard title={inward ? 'To · our unit (receiving)' : toTitle(d)} p={d.to} testId="ewb-review-to">
          {inward && units.length > 0 && <UnitSelect units={units} value={d.unit_id} onChange={set('unit_id')} testId="ewb-to-unit" />}
          {pickDest && <DestinationPicker toSection={d.to_section} unitId={d.to_unit_id} jobber={d.to_jobber} onChange={(v) => setOpts({ ...opts, ...v })} />}
        </PartyCard>
      </div>
      {pickDest && d.dest_assigned && (
        <p className="flex items-center gap-1.5 text-xs text-teal-800" data-testid="ewb-dest-assigned">
          <History className="w-3.5 h-3.5" />Processing unit <b>{d.to_jobber}</b> assigned to this transfer in the Processing section — change it above if needed.
        </p>
      )}
      {pickDest && d.dest_remembered && (
        <p className="flex items-center gap-1.5 text-xs text-teal-800" data-testid="ewb-dest-remembered">
          <History className="w-3.5 h-3.5" />Last used destination for <b>{d.to_section}</b> — change it above if needed.
        </p>
      )}
      {pickDest && d.to_jobber && (
        <p className="text-xs text-gray-600" data-testid="ewb-dest-jobber-note">Going to a jobber: this is a <b>Delivery Challan · Job Work</b> {route === 'tally' ? <> and goes to Tally as <b>Material Out</b> to the jobber's ledger</> : ' to the jobber'}.</p>
      )}
      {d.customer_choices?.length > 0 && <CustomerPick choices={d.customer_choices} value={opts.customer_id} onChange={set('customer_id')} />}
      <ItemsTable items={d.items} totals={d.totals} />
    </div>
  );
};

export default EwbReview;
